import React, {useMemo} from 'react';
import {Table, Button, Dropdown, Input, Grid, Checkbox, Icon} from 'semantic-ui-react';
import {useTable, usePagination, useFilters} from 'react-table';
import {invoicesPageSizesCustomOrders} from '../../common/constants/lists';
import {Filter, DefaultColumnFilter} from './tasksTableFilters';
import {getDropdownOptions} from '../../common/util/dropdown';

const TasksTable = ({tableColumns, tableData, edit, add}) => {
  const data = useMemo(() => tableData, [tableData]);
  const columns = useMemo(() => tableColumns, []);

  const TablePaginator = ({
    page,
    canPreviousPage,
    pageIndex,
    pageOptions,
    pageSize,
    canNextPage,
    nextPage,
    gotoPage,
    previousPage,
    setPageSize,
  }) => {
    if (page.length) {
      return (
        <Grid className="pagination">
          <Grid.Row>
            <Grid.Column width={4} textAlign="left">
              <Button
                className="navigation-button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                content="Anterior"
              />
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <span>páginas </span>
              <span>
                <Input
                  className="pages-input"
                  type="number"
                  value={pageIndex + 1}
                  min="1"
                  max={pageOptions.length}
                  onChange={(e, {value}) => {
                    const page = value ? Number(value) - 1 : 0;
                    if (page < 1 || page > pageOptions.length) {
                      e.target.value = 1;
                    }
                    gotoPage(page);
                  }}
                />
              </span>{' '}
              <span>de {pageOptions.length}</span>
            </Grid.Column>
            <Grid.Column width={4} textAlign="left">
              <Dropdown
                className="page-sizes-selection"
                selection
                upward
                compact
                value={pageSize}
                options={invoicesPageSizesCustomOrders}
                onChange={(e, {value}) => {
                  setPageSize(Number(value));
                }}
              />
              <span> filas</span>
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Button
                className="navigation-button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                content="Siguiente"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }
    return null;
  };

  const renderStatusCell = (row) => {
    return (
      <div style={{'textAlign': 'center'}}>
        <Checkbox toggle checked={row.original.status === 'A' ? true : false} className='green'
          onChange={(e, input) => edit.disableTask(e, input, row.original)} />
      </div>
    );
  };

  const onChangeEditTaskInput = (e, key, input) => {
    e.preventDefault();
    if (input.id === 'stdFlat') {
      const reg = new RegExp('^[0-9]+([.][0-9]{0,2})?$');
      if (input.value && (!reg.test(input.value))) {
        return;
      }
    }
    edit.setTaskToEdit({...edit.taskToEdit, [key]: input.value.toUpperCase()});
    edit.setLastInputChanged({'inputId': input.id, 'caretPos': e.target.selectionStart});
  };

  const setAddTaskValue = (e, input) => {
    e.preventDefault();
    if (input.id === 'area') {
      const taskArea = add.logisticTasksAreas.find((x) => x.uniqueId === input.value);
      add.setAddTask({...add.addTask,
        'uniqueId': input.value,
        'serviceCode1': taskArea && taskArea.primaryCode,
        'serviceCode2': taskArea && taskArea.secondaryCode});
    } else {
      if (input.id === 'stdFlat') {
        const reg = new RegExp('^[0-9]+([.][0-9]{0,2})?$');
        if (input.value && (!reg.test(input.value))) {
          return;
        }
      }
      add.setAddTask({...add.addTask, [input.id]: input.value.toUpperCase()});
    }
  };

  const validateAddTask = () => {
    return add.addTask && (!add.addTask.serviceCode1 ||
      !add.addTask.serviceCode1 || !add.addTask.description);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
      'autoResetFilters': false,
    },
    useFilters,
    usePagination,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns.map((column, i) => {
          if (edit.taskToEdit && (column.id === 'description' || column.id === 'stdFlat')) {
            return {
              ...column,
              'Cell': (item) => {
                return item.row.original.uniqueId === edit.taskToEdit.uniqueId ?
                  <Input fluid id={item.column.id} ref={edit.setRef}
                    onChange={(e, input) => onChangeEditTaskInput(e, item.column.id, input)}
                    value={column.id === 'description' ? edit.taskToEdit.description : edit.taskToEdit.stdFlat} /> :
                  <div>{item.value}</div>;
              },
            };
          }
          if (column.id === 'status') {
            return {
              ...column,
              'width': 1,
              'Cell': ({row}) => renderStatusCell(row),
              'Filter': () => null,
            };
          }
          return column;
        }),
        {
          'Header': '',
          'id': 'edit-col',
          'width': 1,
          'Cell': ({row}) => {
            return edit.taskToEdit && row.original.uniqueId === edit.taskToEdit.uniqueId ?
              <div className='flex middle center'>
                <div style={{'textAlign': 'center'}}>
                  <Icon className='save' link color='green' size='big'
                    onClick={(e) => edit.editTask(e)} />
                </div>
                <div style={{'textAlign': 'center'}}>
                  <Icon className='cancel' link color='red' size='big'
                    onClick={() => edit.setTaskToEdit(null)} />
                </div>
              </div> :
              <div style={{'textAlign': 'center'}}>
                <Icon className='write' color='red' link size='big'
                  onClick={() => edit.setTaskToEdit(row.original)} />
              </div>;
          },
          'Filter': () => null,
        },
      ]);
    }
  );

  return (
    <>
      <Table className='interactive' striped {...getTableProps()}
        style={{'overflow': 'auto'}}>
        <Table.Header fullWidth>
          {headerGroups.map((headerGroup, i) => (
            <Table.Row key={`headers-row-${i}`} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => (
                <Table.HeaderCell
                  key={`title-${index}`}
                  textAlign='center'
                  {...column.getHeaderProps()}
                  width={column.width}
                >
                  {column.render('Header')}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          ))}
          {headerGroups.map((headerGroup, i) => (
            <>
              <Table.Row key={`filters-row-${i}`} id='filters'>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`filter-${index}`}
                    textAlign='center'
                  >
                    {<Filter column={column} />}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
              {add.addTask &&
                <Table.Row key={`new-register${i}`} id='new-register'>
                  {headerGroup.headers.map((column, index) => (
                    <Table.HeaderCell
                      style={{'backgroundColor': 'white'}}
                      key={`filter-${index}`}
                      textAlign='center'>
                      {!(column.id === 'edit-col') ?
                        column.id === 'status' ? <></> :
                          column.id === 'area' ?
                            <Dropdown fluid selection value={add.addTask.uniqueId} id={column.id}
                              options={getDropdownOptions(add.logisticTasksAreas, 'uniqueId', 'description')}
                              onChange={(e, input) => setAddTaskValue(e, input)} /> :
                            <Input fluid id={column.id} ref={edit.setRef}
                              value={add.addTask[column.id]}
                              onChange={(e, input) => setAddTaskValue(e, input)}
                            /> :
                        <div className='flex middle'>
                          <Icon name='save' color='green' size='big'
                            link={!validateAddTask()} disabled={validateAddTask()}
                            onClick={(e) => add.createTask(e)} />
                          <Icon name='cancel' color='red' link size='big'
                            onClick={(e) => add.setAddTask(null)} />
                        </div>
                      }
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              }
            </>
          ))}
        </Table.Header>
        <Table.Body {...getTableBodyProps()}>
          {!page.length && (
            <Table.Row>
              <Table.Cell colSpan={5} textAlign='center'>
                No hay datos disponibles
              </Table.Cell>
            </Table.Row>
          )}
          {page.length > 0 &&
            page.map((row, i) => {
              prepareRow(row);
              return (
                <Table.Row key={i} {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <Table.Cell textAlign='center' {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })}
        </Table.Body>
      </Table>
      <TablePaginator
        page={page}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default TasksTable;
